import { useCallback, useEffect, useRef, useState } from "react";
import { fetchEngagement, fetchEngagementSummary } from "@/api/engagement/api";
import type { EngagementEvent, EngagementFilters, EngagementSummaryGroup } from "@/types/engagement";

type SummaryFilters = Pick<EngagementFilters, "companyIds" | "userIds" | "from" | "to" | "types" | "search">;

export function useEngagementSummary() {
  const [groups, setGroups] = useState<EngagementSummaryGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [typeItems, setTypeItems] = useState<Map<string, EngagementEvent[]>>(new Map());
  const [typeCursors, setTypeCursors] = useState<Map<string, string | null>>(new Map());
  const [typeHasMore, setTypeHasMore] = useState<Map<string, boolean>>(new Map());
  const [typeLoading, setTypeLoading] = useState<Set<string>>(new Set());
  const [typeErrors, setTypeErrors] = useState<Set<string>>(new Set());
  const requestId = useRef(0);
  const typeRequestGeneration = useRef(0);

  const reset = useCallback(() => {
    requestId.current++;
    typeRequestGeneration.current++;
    setGroups([]);
    setLoading(false);
    setError(null);
    setTypeItems(new Map());
    setTypeCursors(new Map());
    setTypeHasMore(new Map());
    setTypeLoading(new Set());
    setTypeErrors(new Set());
  }, []);

  const loadSummary = useCallback(async (filters: SummaryFilters) => {
    const currentRequestId = ++requestId.current;
    setLoading(true);
    setGroups([]);
    setError(null);

    try {
      const data = await fetchEngagementSummary(filters);
      if (currentRequestId !== requestId.current) return;
      setGroups(data.groups ?? []);
    } catch {
      if (currentRequestId === requestId.current) {
        setError("Failed to load Engagement summary.");
      }
    } finally {
      if (currentRequestId === requestId.current) {
        setLoading(false);
      }
    }
  }, []);

  const loadTypeItems = useCallback(async (
    itemKey: string,
    filters: EngagementFilters,
    append = false
  ) => {
    const requestGeneration = typeRequestGeneration.current;
    setTypeLoading(prev => new Set(prev).add(itemKey));
    setTypeErrors(prev => {
      const next = new Set(prev);
      next.delete(itemKey);
      return next;
    });

    try {
      const data = await fetchEngagement(filters);
      if (requestGeneration !== typeRequestGeneration.current) return;

      setTypeItems(prev => {
        const next = new Map(prev);
        next.set(itemKey, append ? [...(prev.get(itemKey) ?? []), ...data.items] : data.items);
        return next;
      });
      setTypeCursors(prev => new Map(prev).set(itemKey, data.nextCursor ?? null));
      setTypeHasMore(prev => new Map(prev).set(itemKey, data.hasMore));
    } catch {
      if (requestGeneration === typeRequestGeneration.current) {
        setTypeErrors(prev => new Set(prev).add(itemKey));
      }
    } finally {
      if (requestGeneration === typeRequestGeneration.current) {
        setTypeLoading(prev => {
          const next = new Set(prev);
          next.delete(itemKey);
          return next;
        });
      }
    }
  }, []);

  useEffect(() => () => {
    requestId.current++;
    typeRequestGeneration.current++;
  }, []);

  return {
    groups,
    loading,
    error,
    typeItems,
    typeCursors,
    typeHasMore,
    typeLoading,
    typeErrors,
    loadSummary,
    loadTypeItems,
    reset,
  };
}